/**
 * theme.js — lê CSS custom properties do :root em runtime.
 *
 * Por que isso existe:
 *   Bibliotecas de gráfico (Recharts, SVG inline) precisam de valores
 *   de cor concretos, não de strings "var(--blue)". Este utilitário
 *   lê o valor computado do token no momento da renderização, garantindo
 *   que o theming por professor funcione automaticamente.
 *
 * Uso:
 *   import { getCssVar } from "../utils/theme";
 *   const blue = getCssVar("--blue");           // "#1d9e75" (ou cor do professor)
 *   const rgb  = getCssVar("--accent-rgb");     // "29, 158, 117"
 */

/**
 * Retorna o valor computado de uma CSS custom property do :root.
 * @param {string} variable  Nome da variável, com "--" (ex: "--blue")
 * @param {string} fallback  Valor padrão caso a variável não exista
 * @returns {string}
 */
export function getCssVar(variable, fallback = "") {
  if (typeof window === "undefined") return fallback;
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim() || fallback
  );
}

/**
 * Converte um hex #RRGGBB em { r, g, b }.
 */
export function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/**
 * Escurece um canal de cor por `amount` (0–1).
 */
function darken(value, amount) {
  return Math.max(0, Math.round(value * (1 - amount)));
}

/**
 * Converte { r, g, b } de volta para string hex #RRGGBB.
 */
function rgbToHex({ r, g, b }) {
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

/**
 * Aplica a identidade visual do professor ao :root da página.
 * Seta --blue, --blue-hover e --accent-rgb a partir de um hex.
 * Todos os rgba(var(--accent-rgb), X) e var(--blue) do CSS herdam
 * automaticamente sem nenhuma outra mudança.
 *
 * @param {string|null} hex  Cor no formato "#RRGGBB". Null reseta para o padrão.
 */
/** Luminância relativa WCAG (0 = preto, 1 = branco) */
function linearize(c) {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}
export function luminance({ r, g, b }) {
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/** Razão de contraste WCAG entre duas cores hex #RRGGBB (1–21). */
export function contrastRatio(hexA, hexB) {
  const la = luminance(hexToRgb(hexA));
  const lb = luminance(hexToRgb(hexB));
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Contraste de um acento contra o fundo padrão da aplicação (--bg). */
export function accentContrast(hex) {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex || "")) return 21;
  const bg = (getCssVar("--bg", "#0f172a") || "#0f172a").trim();
  const bgHex = /^#[0-9A-Fa-f]{6}$/.test(bg) ? bg : "#0f172a";
  return contrastRatio(hex, bgHex);
}

/** Converte { r, g, b } (0–255) em { h, s, l } (0–1). */
function rgbToHsl({ r, g, b }) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
    case gn: h = (bn - rn) / d + 2; break;
    default: h = (rn - gn) / d + 4;
  }
  return { h: h / 6, s, l };
}

/** Converte { h, s, l } (0–1) em { r, g, b } (0–255). */
function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
}

/**
 * Uma cor com boa presença sobre fundo escuro pode "lavar" contra fundo
 * claro: mesma luminância relativa, mas contraste bem menor contra um fundo
 * claro do que contra um fundo quase-preto. Escurece/satura a cor
 * (preservando o matiz) até atingir o contraste alvo -- ou CLAREIA, se o
 * fundo de referência for escuro (ver bgIsLight abaixo).
 *
 * Antes disso, sempre checava contra "#ffffff" fixo e só rodava em
 * `isLight` (tema claro) -- cego pro bg livre do professor: um bg custom
 * claro no slot ESCURO (ou vice-versa) fazia o acento não ser corrigido
 * nenhuma vez (tema="dark" pulava a correção) ou ser corrigido contra o
 * fundo errado (branco fixo, não o custom). Agora recebe o hex do fundo
 * REAL (bgHex) -- default do tema ou o bg custom já resolvido pelo
 * chamador -- e decide sozinha se/como corrigir, olhando o contraste de
 * verdade contra ele. Sem bgHex, mantém o comportamento antigo (assume
 * branco) como fallback seguro.
 *
 * @param {string} hex        Cor de marca "#RRGGBB".
 * @param {string} [bgHex]    Fundo real de referência "#RRGGBB" (default "#ffffff").
 * @param {number} [targetRatio]
 */
export function ensureAccentContrast(hex, bgHex = "#ffffff", targetRatio = 4.5) {
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex || "")) return hex;
  const safeBg = /^#[0-9A-Fa-f]{6}$/.test(bgHex || "") ? bgHex : "#ffffff";
  if (contrastRatio(hex, safeBg) >= targetRatio) return hex;
  const hsl = rgbToHsl(hexToRgb(hex));
  const h = hsl.h;
  const s = Math.min(1, hsl.s * 1.15);
  let l = hsl.l;
  // Fundo claro precisa de acento mais escuro pra ganhar contraste; fundo
  // escuro precisa do oposto -- mesma lógica de "andar pro lado certo" já
  // usada em deriveSurfaceBase (SURFACE_STEP_CEILING).
  const bgIsLight = luminance(hexToRgb(safeBg)) > 0.5;
  const floor = bgIsLight ? 0.12 : 0.88;
  const step = bgIsLight ? -0.01 : 0.01;
  const keepGoing = () => (bgIsLight ? l > floor : l < floor);
  while (keepGoing()) {
    l += step;
    const candidateHex = rgbToHex(hslToRgb({ h, s, l }));
    if (contrastRatio(candidateHex, safeBg) >= targetRatio) return candidateHex;
  }
  return rgbToHex(hslToRgb({ h, s, l: floor }));
}

/**
 * Hex do bg PADRÃO do app (sem customização) pro tema dado -- espelha
 * SURFACE_BASE.{dark,light}.bg. Usado como referência de contraste do
 * acento quando o professor/aluno não tem bg custom pro slot em questão.
 * @param {"dark"|"light"} theme
 */
export function defaultBgHex(theme) {
  return rgbToHex(theme === "light" ? SURFACE_BASE.light.bg : SURFACE_BASE.dark.bg);
}

/**
 * Prevê a cor de marca como ela VAI renderizar de verdade num modo
 * específico -- mesma correção que applyProfessorTheme aplica no acento,
 * exposta pra UI mostrar uma prévia (em vez de bloquear salvar com um
 * corte arbitrário). @param {string} rawHex cor escolhida (sem correção).
 * @param {"dark"|"light"} theme
 * @param {string|null} [customBgHex] bg custom já resolvido pro modo, se houver.
 */
export function previewAccentHex(rawHex, theme, customBgHex) {
  if (!/^#[0-9A-Fa-f]{6}$/.test(rawHex || "")) return null;
  const bgHex = customBgHex && /^#[0-9A-Fa-f]{6}$/.test(customBgHex) ? customBgHex : defaultBgHex(theme);
  return ensureAccentContrast(rawHex, bgHex);
}

/**
 * Interpola matiz (0–1, espaço circular) pelo caminho mais curto.
 * Ex.: lerpHue(0.02, 0.95, 0.5) anda por perto de 0 (vermelho), não
 * atravessa o círculo inteiro até o outro lado.
 */
function lerpHue(h1, h2, t) {
  let diff = h2 - h1;
  if (diff > 0.5) diff -= 1;
  if (diff < -0.5) diff += 1;
  let h = h1 + diff * t;
  if (h < 0) h += 1;
  if (h >= 1) h -= 1;
  return h;
}

/**
 * Tinge uma cor de superfície neutra (fundo/card) com o matiz da cor de
 * marca do professor — dá presença à marca nas áreas que hoje são sempre
 * cinza/navy fixo, sem reintroduzir um "fundo exato" separado (isso foi
 * tentado e revertido — ver docs/07-issues.md R242). Mantém a LUMINOSIDADE
 * (l) da superfície original intacta de propósito: o contraste de texto já
 * validado pra essa superfície continua valendo sem precisar resolver
 * contraste de novo, já que a luminância WCAG segue de perto a
 * luminosidade HSL pro blendRatio baixo usado aqui (TINT_RATIO).
 *
 * @param {{r:number,g:number,b:number}} baseRgb   cor de superfície original
 * @param {string} accentHex  cor de marca do professor (#RRGGBB)
 * @param {number} blendRatio 0–1, quanto do matiz/saturação do acento entra
 */
function tintTowardAccent(baseRgb, accentHex, blendRatio) {
  const baseHsl = rgbToHsl(baseRgb);
  const accentHsl = rgbToHsl(hexToRgb(accentHex));
  let h, s;
  if (baseHsl.s < 0.05) {
    // Base quase sem saturação (branco/cinza do tema claro) não tem matiz
    // real pra interpolar — usa o matiz do acento direto, com saturação
    // baixa (senão vira um card colorido em vez de só "tingido").
    h = accentHsl.h;
    s = Math.min(0.35, accentHsl.s * blendRatio * 1.4);
  } else {
    // Base já tem cor própria (ex.: navy do dark, que tem ~0.45 de
    // saturação) — interpola matiz e aproxima a saturação com mais cautela.
    h = lerpHue(baseHsl.h, accentHsl.h, blendRatio);
    s = baseHsl.s + (accentHsl.s - baseHsl.s) * blendRatio * 0.6;
  }
  // Branco puro (l=1.0) é matematicamente "cego" a matiz/saturação em HSL
  // (qualquer h/s em l=1 resolve pra branco de novo) -- sem isso,
  // --surface-primary do tema claro (que é #ffffff exato) nunca mostraria
  // tingimento nenhum. Um teto de 0.97 dá espaço pro matiz aparecer sem
  // deixar de "ler" como branco/quase-branco.
  const l = Math.min(baseHsl.l, 0.97);
  return hslToRgb({ h, s: Math.max(0, Math.min(1, s)), l });
}

// Cor "crua" de cada superfície por tema, espelhando theme.css
// ([data-theme="dark"]/[data-theme="light"]) — se algum dia o valor mudar
// lá, muda aqui também pra não dessincronizar a base do tingimento.
const SURFACE_BASE = {
  dark: {
    bg: { r: 9, g: 14, b: 28 },
    surfacePrimary: { rgb: { r: 26, g: 39, b: 68 }, a: 0.96 },
    surfaceSecondary: { rgb: { r: 26, g: 39, b: 68 }, a: 0.92 },
    surfaceTertiary: { rgb: { r: 26, g: 39, b: 68 }, a: 0.85 },
  },
  light: {
    bg: { r: 238, g: 241, b: 246 },
    surfacePrimary: { rgb: { r: 255, g: 255, b: 255 }, a: 1 },
    surfaceSecondary: { rgb: { r: 248, g: 249, b: 250 }, a: 1 },
    surfaceTertiary: { rgb: { r: 240, g: 241, b: 243 }, a: 1 },
  },
};

// 12% de mistura de matiz/saturação — sutil o bastante pra não parecer um
// "reskin" nem arriscar contraste, mas visível (testado contra --text-primary
// em 4 cores de marca bem diferentes antes de fixar esse número).
const TINT_RATIO = 0.12;

// ─────────────────────────────────────────────────────────────────────────
// Bg livre por professor (extensao de SURFACE_BASE) -- vira um terceiro par
// de bg/surface possivel, plugado no MESMO tintTowardAccent que ja tinge
// dark/light hoje. Um valor por modo do toggle do aluno (dark/light),
// independentes entre si -- ver applyProfessorTheme.
// ─────────────────────────────────────────────────────────────────────────

// Degrau de luminosidade (HSL) entre bg e surface, calibrado pra ficar perto
// da relacao ja existente entre SURFACE_BASE.dark.bg (9,14,28) e
// SURFACE_BASE.dark.surfacePrimary (26,39,68) -- l ~= 0.0725 -> l ~= 0.1843,
// delta ~= 0.11.
const SURFACE_STEP = 0.10;
// Acima desse l (HSL), um bg escolhido pelo professor ja esta perto demais
// do branco pra "clarear mais" sem estourar/dessaturar em branco puro (mesmo
// problema que tintTowardAccent trata pro tema claro) -- inverte a direcao
// (escurece) nesse caso. A diferenciacao visual do card nessa faixa passa a
// contar mais com --shadow-*/--border-*, que os componentes ja usam de
// qualquer forma.
const SURFACE_STEP_CEILING = 0.90;

/**
 * Deriva uma base de superficies (bg + 3 niveis) a partir de UMA cor de bg
 * livre, escolhida pelo professor -- generaliza a relacao que ja existe
 * dentro de SURFACE_BASE.dark: os 3 niveis de surface sao a MESMA cor, um
 * degrau mais clara (ou mais escura, perto do teto) que o bg, layerizada em
 * opacidade decrescente (0.96/0.92/0.85, igual aos 2 modos fixos).
 * @param {string} bgHex "#RRGGBB"
 */
function deriveSurfaceBase(bgHex) {
  const bgRgb = hexToRgb(bgHex);
  const hsl = rgbToHsl(bgRgb);
  const targetL = hsl.l > SURFACE_STEP_CEILING
    ? Math.max(0, hsl.l - SURFACE_STEP)
    : Math.min(1, hsl.l + SURFACE_STEP);
  const surfaceRgb = hslToRgb({ h: hsl.h, s: hsl.s, l: targetL });
  return {
    bg: bgRgb,
    surfacePrimary: { rgb: surfaceRgb, a: 0.96 },
    surfaceSecondary: { rgb: surfaceRgb, a: 0.92 },
    surfaceTertiary: { rgb: surfaceRgb, a: 0.85 },
  };
}

// Familia de texto por bg livre -- espelha os tokens de texto ja definidos e
// testados (contraste WCAG comentado ali) em theme.css [data-theme="dark"] e
// [data-theme="light"]. Reaproveitados aqui, nao recalculados do zero: um bg
// de professor "claro demais" pro texto claro do dark continuar legivel cai
// automaticamente na familia de texto escura do light, e vice-versa -- mesma
// rede de seguranca que ensureAccentContrast ja da pro acento, agora tambem
// pro texto do card/pagina.
// Apesar do nome (histórico -- começou só com texto), também carrega
// --border-*: mesmo vazamento, mesmo mecanismo. --text-0-70/60/50/40 e
// --border/-dark/-md/-strong/-0-XX ficavam presos ao valor do data-theme
// ATUAL (hardcoded em cada bloco de theme.css), nunca tocados por
// applyProfessorTheme -- só --text/--muted/--subtle/--text-primary(-rgb)
// eram cobertos. Passou batido enquanto bg custom só testava cores "no
// meio da escala" (--text-0-* mudar de opacidade não é visível o
// suficiente pra notar) -- ficou óbvio só com um bg extremo (branco no
// slot escuro): --text-0-50 etc continuavam rgba(225,245,238,X) do tema
// dark ativo, ou seja texto claro translúcido sobre fundo agora branco,
// virando ilegível -- exatamente os captions/legendas/labels secundários
// (StreakCard .kicker/.hint, legendas de gráfico, subtítulo do clima),
// enquanto texto primário (--text-primary, já coberto) continuava OK.
// Valores espelham theme.css [data-theme="dark"]/[data-theme="light"]
// (não :root -- root e o bloco dark divergem em alguns, ex. --border:
// 0.08 no root vs 0.11 no bloco dark explícito, que é o que realmente
// vale com data-theme="dark" setado).
const TEXT_FAMILY = {
  dark: {
    "--text": "#e1f5ee",
    "--muted": "rgba(225, 245, 238, 0.60)",
    "--subtle": "rgba(225, 245, 238, 0.38)",
    "--app-text": "#e1f5ee",
    "--app-muted": "rgba(225, 245, 238, 0.6)",
    "--text-primary": "#e1f5ee",
    "--text-primary-rgb": "225, 245, 238",
    "--text-secondary": "#a0b0c0",
    "--text-muted": "#6b7280",
    "--text-0-70": "rgba(225, 245, 238, 0.70)",
    "--text-0-60": "rgba(225, 245, 238, 0.60)",
    "--text-0-50": "rgba(225, 245, 238, 0.50)",
    "--text-0-40": "rgba(225, 245, 238, 0.40)",
    "--border": "rgba(255, 255, 255, 0.11)",
    "--border-dark": "rgba(255, 255, 255, 0.06)",
    "--border-md": "rgba(255, 255, 255, 0.16)",
    "--border-strong": "rgba(255, 255, 255, 0.20)",
    "--border-0-04": "rgba(255, 255, 255, 0.04)",
    "--border-0-05": "rgba(255, 255, 255, 0.05)",
    "--border-0-06": "rgba(255, 255, 255, 0.06)",
    "--border-0-08": "rgba(255, 255, 255, 0.08)",
    "--border-0-10": "rgba(255, 255, 255, 0.10)",
    "--border-0-15": "rgba(255, 255, 255, 0.15)",
    "--border-0-16": "rgba(255, 255, 255, 0.16)",
    "--border-0-18": "rgba(255, 255, 255, 0.18)",
    "--border-0-20": "rgba(255, 255, 255, 0.20)",
    "--border-0-22": "rgba(255, 255, 255, 0.22)",
    "--border-0-35": "rgba(255, 255, 255, 0.35)",
    // Sombra/feedback/"accent" fixos (institucional) -- também presos ao
    // data-theme atual, não ao family real do bg custom. --text-accent e
    // --feedback-info ficam de propósito no teal institucional mesmo com
    // accent customizado (ver comentário em WorkoutAdminModal.jsx sobre
    // --text-accent marcar "padrão da plataforma") -- só o par claro/escuro
    // certo pro contraste muda aqui, nunca a cor de marca do professor.
    "--shadow-sm": "0 6px 18px rgba(0, 0, 0, 0.28)",
    "--shadow-md": "0 14px 40px rgba(0, 0, 0, 0.40)",
    "--shadow-lg": "0 24px 70px rgba(0, 0, 0, 0.55)",
    "--feedback-warning": "#f59e0b",
    "--text-accent": "#1d9e75",
    "--feedback-info": "#1d9e75",
  },
  light: {
    "--text": "#1a1a1a",
    "--muted": "rgba(26, 26, 26, 0.72)",
    "--subtle": "rgba(26, 26, 26, 0.65)",
    "--app-text": "#1a1a1a",
    "--app-muted": "rgba(26, 26, 26, 0.6)",
    "--text-primary": "#1a1a1a",
    "--text-primary-rgb": "26, 26, 26",
    "--text-secondary": "#4a5568",
    "--text-muted": "#6b7280",
    "--text-0-70": "rgba(26, 26, 26, 0.85)",
    "--text-0-60": "rgba(26, 26, 26, 0.78)",
    "--text-0-50": "rgba(26, 26, 26, 0.70)",
    "--text-0-40": "rgba(26, 26, 26, 0.65)",
    "--border": "rgba(0, 0, 0, 0.08)",
    "--border-dark": "rgba(0, 0, 0, 0.10)",
    "--border-md": "rgba(0, 0, 0, 0.12)",
    "--border-strong": "rgba(0, 0, 0, 0.15)",
    "--border-0-04": "rgba(0, 0, 0, 0.04)",
    "--border-0-05": "rgba(0, 0, 0, 0.05)",
    "--border-0-06": "rgba(0, 0, 0, 0.06)",
    "--border-0-08": "rgba(0, 0, 0, 0.08)",
    "--border-0-10": "rgba(0, 0, 0, 0.10)",
    "--border-0-15": "rgba(0, 0, 0, 0.15)",
    "--border-0-16": "rgba(0, 0, 0, 0.16)",
    "--border-0-18": "rgba(0, 0, 0, 0.18)",
    "--border-0-20": "rgba(0, 0, 0, 0.20)",
    "--border-0-22": "rgba(0, 0, 0, 0.22)",
    "--border-0-35": "rgba(0, 0, 0, 0.35)",
    "--shadow-sm": "0 6px 18px rgba(0, 0, 0, 0.08)",
    "--shadow-md": "0 14px 40px rgba(0, 0, 0, 0.12)",
    "--shadow-lg": "0 24px 70px rgba(0, 0, 0, 0.15)",
    "--feedback-warning": "#b45309",
    "--text-accent": "#16795a",
    "--feedback-info": "#16795a",
  },
};
function applyTextFamily(root, family) {
  const tokens = TEXT_FAMILY[family] || TEXT_FAMILY.dark;
  Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));
}
function resetTextFamily(root) {
  Object.keys(TEXT_FAMILY.dark).forEach((k) => root.style.removeProperty(k));
}

/**
 * Decide qual família de texto (clara ou escura) dá MAIS CONTRASTE contra um
 * bg livre escolhido pelo professor -- fonte única usada tanto por
 * applyProfessorTheme (pra aplicar de verdade) quanto pela prévia do
 * color-picker no admin (UserAdminModal), pra nunca divergirem.
 *
 * Antes disso, a decisão usava luminance(bgRgb) > 0.5 (luminância relativa
 * WCAG) como corte. Esse número parece "meio a meio", mas WCAG luminância
 * NÃO é linear com o que o olho vê como "claro": um cinza que já parece bem
 * claro (#b6b6b6, ~71% de luminosidade HSL -- o que o professor via na tela
 * como "quase branco") tem luminância relativa WCAG de só ~0.468, ou seja,
 * cai do lado ERRADO de um corte em 0.5 -- e recebia texto claro (família
 * "dark") sobre fundo claro, quase ilegível. Comparar o contraste real das
 * DUAS opções de texto (em vez de aplicar um corte de luminância) resolve
 * isso pra qualquer cor, não só pra esse caso específico -- sempre escolhe
 * a opção que realmente lê melhor, que é o objetivo real aqui.
 *
 * @param {string} bgHex "#RRGGBB"
 * @returns {{ family: "dark"|"light", textHex: string }}
 */
export function pickTextFamilyForBg(bgHex) {
  if (!/^#[0-9A-Fa-f]{6}$/.test(bgHex || "")) {
    return { family: "dark", textHex: TEXT_FAMILY.dark["--text"] };
  }
  const lightTextContrast = contrastRatio(bgHex, TEXT_FAMILY.dark["--text"]);
  const darkTextContrast = contrastRatio(bgHex, TEXT_FAMILY.light["--text"]);
  return lightTextContrast > darkTextContrast
    ? { family: "dark", textHex: TEXT_FAMILY.dark["--text"] }
    : { family: "light", textHex: TEXT_FAMILY.light["--text"] };
}

// Segunda familia de tokens de superficie -- "aliases de compatibilidade"
// (--surface/--surface-strong/--surface-solid) e os tokens rgba
// pre-combinados (--surface-<N>-<alpha>, gerados historicamente pra evitar
// escrever rgba(var(...), X) em cada CSS module). Nenhum desses nunca foi
// tocado por applyProfessorTheme -- ficavam presos no literal fixo do tema,
// mesmo com brand_color tingindo --surface-primary/secondary/tertiary ao
// lado. Reconstruidos aqui a partir do MESMO tom de superficie ja derivado
// (surfaceRgb), cada um na sua alpha original -- fecha o vazamento sem
// precisar caçar cada CSS module que os usa direto.
const SURFACE_FAMILY_KEYS = [
  "--surface", "--surface-strong", "--surface-solid",
  "--surface-0-98", "--surface-0-97", "--surface-0-60",
  "--surface-26-80", "--surface-26-85", "--surface-17-90",
];
function applySurfaceFamily(root, bgRgb, surfaceRgb) {
  root.style.setProperty("--bg-rgb", `${bgRgb.r}, ${bgRgb.g}, ${bgRgb.b}`);
  root.style.setProperty("--surface-primary-rgb", `${surfaceRgb.r}, ${surfaceRgb.g}, ${surfaceRgb.b}`);
  root.style.setProperty("--surface-solid-rgb", `${surfaceRgb.r}, ${surfaceRgb.g}, ${surfaceRgb.b}`);
  const rgba = (a) => `rgba(${surfaceRgb.r}, ${surfaceRgb.g}, ${surfaceRgb.b}, ${a})`;
  root.style.setProperty("--surface", rgba(0.85));
  root.style.setProperty("--surface-strong", rgba(0.95));
  root.style.setProperty("--surface-solid", rgba(0.97));
  root.style.setProperty("--surface-0-98", rgba(0.98));
  root.style.setProperty("--surface-0-97", rgba(0.97));
  root.style.setProperty("--surface-0-60", rgba(0.60));
  root.style.setProperty("--surface-26-80", rgba(0.80));
  root.style.setProperty("--surface-26-85", rgba(0.85));
  root.style.setProperty("--surface-17-90", rgba(0.90));
}
function resetSurfaceFamily(root) {
  root.style.removeProperty("--bg-rgb");
  root.style.removeProperty("--surface-primary-rgb");
  root.style.removeProperty("--surface-solid-rgb");
  SURFACE_FAMILY_KEYS.forEach((k) => root.style.removeProperty(k));
}

// --border-accent, --border-focus e --accent-0-12/15/18 -- halos/bordas
// tingidos com a cor de marca, usados lado a lado com --blue/--accent-rgb
// nos mesmos componentes (ex.: AdminDashboard.module.css:
// "background: var(--accent-0-15); color: var(--blue);"). --border-accent
// já referenciava var(--accent-rgb) (acompanha accent custom certo) -- só a
// OPACIDADE ficava presa ao data-theme, não ao family real do bg custom
// (mesmo problema do TEXT_FAMILY acima). --border-focus e --accent-0-* eram
// literais fixos no teal institucional, sem nenhuma das duas coisas -- bug
// dobrado: nem acompanhavam a cor do professor nem o family certo. Os
// valores aqui usam var(--accent-rgb) em vez de expandir pro RGB numérico --
// assim herdam automaticamente accent custom OU o institucional (que já tem
// seu próprio par claro/escuro em theme.css), só a opacidade é decidida
// aqui pelo family.
function applyAccentOpacityFamily(root, isLightFamily) {
  const rgb = "var(--accent-rgb)";
  if (isLightFamily) {
    root.style.setProperty("--border-accent", `rgba(${rgb}, 0.25)`);
    root.style.setProperty("--border-focus", `rgba(${rgb}, 0.3)`);
    root.style.setProperty("--accent-0-12", `rgba(${rgb}, 0.20)`);
    root.style.setProperty("--accent-0-15", `rgba(${rgb}, 0.24)`);
    root.style.setProperty("--accent-0-18", `rgba(${rgb}, 0.28)`);
  } else {
    root.style.setProperty("--border-accent", `rgba(${rgb}, 0.18)`);
    root.style.setProperty("--border-focus", `rgba(${rgb}, 0.3)`);
    root.style.setProperty("--accent-0-12", `rgba(${rgb}, 0.12)`);
    root.style.setProperty("--accent-0-15", `rgba(${rgb}, 0.15)`);
    root.style.setProperty("--accent-0-18", `rgba(${rgb}, 0.18)`);
  }
}
function resetAccentOpacityFamily(root) {
  ["--border-accent", "--border-focus", "--accent-0-12", "--accent-0-15", "--accent-0-18"]
    .forEach((k) => root.style.removeProperty(k));
}

/**
 * Aplica a identidade visual do professor ao :root: cor de marca (acento) e,
 * opcionalmente, um bg exato por modo (dark/light) que substitui a base
 * padrão do app -- ver deriveSurfaceBase. Os dois são independentes: dá pra
 * ter só acento (comportamento de sempre), só bg, ou os dois juntos.
 *
 * @param {string|null} hex   Cor de marca (acento) "#RRGGBB".
 * @param {string} [theme]    'dark' | 'light'. Se omitido, lê data-theme do <html>.
 * @param {{dark?:string|null, light?:string|null}} [bgColors] Bg exato do
 *   professor por modo, já resolvido pelo chamador (professor_bg_color_X ??
 *   own_bg_color_X). Só o valor do modo ATIVO é usado.
 */
export function applyProfessorTheme(hex, theme, bgColors) {
  const root = document.documentElement;
  const isLight = theme ? theme === "light" : root.getAttribute("data-theme") === "light";

  const validHex = hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : null;
  const rawBg = bgColors ? (isLight ? bgColors.light : bgColors.dark) : null;
  const customBgHex = rawBg && /^#[0-9A-Fa-f]{6}$/.test(rawBg) ? rawBg : null;

  if (!validHex && !customBgHex) {
    // Reseta para os valores padrão definidos em App.css/theme.css
    root.style.removeProperty("--blue");
    root.style.removeProperty("--blue-hover");
    root.style.removeProperty("--accent-rgb");
    root.style.removeProperty("--blue-on");
    root.style.removeProperty("--bg");
    root.style.removeProperty("--app-bg");
    root.style.removeProperty("--surface-primary");
    root.style.removeProperty("--surface-secondary");
    root.style.removeProperty("--surface-tertiary");
    root.style.removeProperty("--overlay-rgb");
    resetSurfaceFamily(root);
    resetTextFamily(root);
    resetAccentOpacityFamily(root);
    return;
  }

  // Base de bg/surface: a do professor (livre, derivada) se ele definiu bg
  // pro modo ativo; senão a base padrão do modo de sempre -- SURFACE_BASE
  // continua intocada, zero mudança pra quem nunca customizou bg. Calculada
  // ANTES do acento (abaixo) de propósito -- precisa do bg REAL como
  // referência de contraste, não mais de um fundo branco fixo.
  const base = customBgHex ? deriveSurfaceBase(customBgHex) : (isLight ? SURFACE_BASE.light : SURFACE_BASE.dark);
  const refBgHex = rgbToHex(base.bg);

  // Acento (--blue/--blue-hover/--accent-rgb/--blue-on) -- só roda se tiver
  // cor de marca; sem ela, esses tokens ficam no padrão do CSS.
  let tintHex = null;
  if (validHex) {
    // Corrige contraste contra o bg REAL (custom ou padrão do tema) -- ver
    // ensureAccentContrast acima pro porquê isso deixou de ser "só tema
    // claro contra branco fixo": com bg livre, o fundo que realmente
    // importa pode ser custom e/ou o oposto do que o modo sugere.
    tintHex = ensureAccentContrast(validHex, refBgHex);
    const rgb = hexToRgb(tintHex);
    const { r, g, b } = rgb;
    const hover = rgbToHex({ r: darken(r, 0.3), g: darken(g, 0.3), b: darken(b, 0.3) });
    // Texto sobre o acento: escuro para cores claras, claro para cores escuras
    const blueOn = luminance(rgb) > 0.35 ? "#0a1223" : "#e1f5ee";
    root.style.setProperty("--blue", tintHex);
    root.style.setProperty("--blue-hover", hover);
    root.style.setProperty("--accent-rgb", `${r}, ${g}, ${b}`);
    root.style.setProperty("--blue-on", blueOn);
  } else {
    root.style.removeProperty("--blue");
    root.style.removeProperty("--blue-hover");
    root.style.removeProperty("--accent-rgb");
    root.style.removeProperty("--blue-on");
  }

  // Tinge fundo/superfícies com o matiz da marca (ver tintTowardAccent) —
  // --bg e --app-bg são dois tokens de fundo separados (.page vs <body>,
  // ver App.css) que precisam ficar em sincronia, senão o <body> fica pra
  // trás igual já aconteceu antes (débito registrado, corrigido e depois
  // revertido localmente antes de comitar -- sem issue registrada).
  const bgRgb = tintHex ? tintTowardAccent(base.bg, tintHex, TINT_RATIO) : base.bg;
  const bgHex = rgbToHex(bgRgb);
  root.style.setProperty("--bg", bgHex);
  root.style.setProperty("--app-bg", bgHex);
  const toRgbaString = (layer) => {
    const t = tintHex ? tintTowardAccent(layer.rgb, tintHex, TINT_RATIO) : layer.rgb;
    return `rgba(${t.r}, ${t.g}, ${t.b}, ${layer.a})`;
  };
  root.style.setProperty("--surface-primary", toRgbaString(base.surfacePrimary));
  root.style.setProperty("--surface-secondary", toRgbaString(base.surfaceSecondary));
  root.style.setProperty("--surface-tertiary", toRgbaString(base.surfaceTertiary));

  const surfaceRgb = tintHex ? tintTowardAccent(base.surfacePrimary.rgb, tintHex, TINT_RATIO) : base.surfacePrimary.rgb;
  applySurfaceFamily(root, bgRgb, surfaceRgb);

  if (customBgHex) {
    // Overlay/scrim de modal segue o bg escolhido -- sem isso o fundo por
    // trás de todo modal ficaria sempre no navy padrão mesmo com bg
    // customizado (era exatamente essa inconsistência que quebrava as
    // tentativas anteriores).
    root.style.setProperty("--overlay-rgb", `${bgRgb.r}, ${bgRgb.g}, ${bgRgb.b}`);
    // Família de texto pelo CONTRASTE real contra o bg escolhido, não pelo
    // modo nem por um corte de luminância -- ver pickTextFamilyForBg acima
    // pro porquê (corte fixo já deixou passar um bg claro com texto claro).
    // Garante contraste mesmo se o professor colocar um bg "claro" no slot
    // escuro (ou vice-versa).
    const { family } = pickTextFamilyForBg(rgbToHex(bgRgb));
    applyTextFamily(root, family);
    applyAccentOpacityFamily(root, family === "light");
  } else {
    root.style.removeProperty("--overlay-rgb");
    resetTextFamily(root);
    resetAccentOpacityFamily(root);
  }
}

/**
 * Troca dinamicamente <link rel="manifest">, <link rel="apple-touch-icon">
 * e a meta tag apple-mobile-web-app-title pro ícone/nome do PWA customizado
 * do professor (ou do professor vinculado, se for aluno) — sem isso,
 * "Adicionar à tela inicial" sempre usa o ícone institucional estático em
 * public/manifest.json. Sem app_icon/nome customizado, mantém os valores
 * padrão (idempotente: reseta pros estáticos).
 *
 * A meta tag existe porque o iOS Safari ignora "name"/"short_name" do
 * manifest.json ao sugerir o nome no "Adicionar à Tela de Início" — só
 * respeita apple-mobile-web-app-title (Android/Chrome já lê certo do
 * manifest, então esse ajuste é só pra paridade no Safari).
 *
 * Recebe campos primitivos (não o objeto `user` inteiro) de propósito — isso
 * deixa a lista de dependências do useEffect no call site exaustiva sem
 * warning do eslint-plugin-react-hooks, e deixa explícito exatamente do que
 * a função depende. Nunca lança — qualquer falha aqui não pode quebrar a
 * página (ver docs/07-issues.md).
 *
 * @param {"professor"|"aluno"|"admin"|string|undefined} role
 * @param {number|undefined} userId
 * @param {number|undefined} professorId        professor_id do usuário (se aluno)
 * @param {string|undefined} customAppIconUrl   ícone próprio (se o usuário é professor)
 * @param {string|undefined} professorAppIconUrl ícone herdado do professor vinculado (se aluno)
 * @param {string|undefined} apiUrl             base da API (REACT_APP_API_URL)
 * @param {string|undefined} ownName            nome do próprio usuário (se professor)
 * @param {string|undefined} professorName      nome do professor vinculado (se aluno)
 */
export function applyPwaManifest(role, userId, professorId, customAppIconUrl, professorAppIconUrl, apiUrl, ownName, professorName) {
  try {
    if (typeof document === "undefined") return;
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const touchIconLink = document.querySelector('link[rel="apple-touch-icon"]');
    let titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');

    // Professor usa o próprio ícone/nome; aluno herda do professor vinculado.
    //
    // Pro professor, aceita QUALQUER uma das duas fontes de propósito: o
    // /me devolve `custom_app_icon_url`, mas a resposta do LOGIN não — ela
    // manda o ícone já resolvido em `professor_app_icon_url` (o backend
    // aplica a herança em get_professor_app_icon_url, e pra professor isso é
    // o ícone dele mesmo). Olhando só pra customAppIconUrl, o manifest ficava
    // institucional na janela entre o login e a resposta do /me — e instalar
    // o app nesse intervalo gravava o ícone errado na tela inicial.
    const hasCustomIcon = role === "professor"
      ? !!(customAppIconUrl || professorAppIconUrl)
      : !!professorAppIconUrl;
    const ownerId = role === "professor" ? userId : professorId;
    const displayName = role === "professor" ? ownName : professorName;

    // Cache-busting: a URL do ícone (path completo no servidor, ex.
    // "uploads/app-icons/81_1786853045_x.png") já muda a cada novo upload
    // (o nome do arquivo embute o timestamp), então dá pra usar ela mesma
    // como versão. Sem isso, iOS e navegadores em geral cacheiam o
    // apple-touch-icon de forma bem agressiva pela URL — trocar só o
    // CONTEÚDO da imagem numa URL já visitada não é suficiente pra forçar
    // reload, o ícone velho fica "grudado" até a URL mudar.
    const iconSourceUrl = role === "professor" ? (customAppIconUrl || professorAppIconUrl) : professorAppIconUrl;
    const iconVersion = iconSourceUrl ? encodeURIComponent(iconSourceUrl) : "";

    if (manifestLink || touchIconLink) {
      if (hasCustomIcon && ownerId && apiUrl) {
        if (manifestLink) manifestLink.setAttribute("href", `${apiUrl}/manifest/${ownerId}.json?v=${iconVersion}`);
        if (touchIconLink) touchIconLink.setAttribute("href", `${apiUrl}/manifest/icon/${ownerId}/512.png?v=${iconVersion}`);
      } else {
        // Sem ícone customizado — volta pros estáticos padrão do build
        if (manifestLink) manifestLink.setAttribute("href", "/manifest.json");
        if (touchIconLink) touchIconLink.setAttribute("href", "/brand/icon-512.png");
      }
    }

    // Cria a meta tag se por algum motivo não existir no HTML (defensivo).
    if (!titleMeta) {
      titleMeta = document.createElement("meta");
      titleMeta.setAttribute("name", "apple-mobile-web-app-title");
      document.head.appendChild(titleMeta);
    }
    titleMeta.setAttribute("content", displayName || "TreinoNexo");
  } catch (_) {
    // Nunca deixa isso derrubar a página — pior caso, o ícone/nome do PWA fica o padrão.
  }
}

/** Atalhos para as variáveis mais usadas em gráficos */
export const themeColors = {
  get blue()       { return getCssVar("--blue",       "#1d9e75"); },
  get blueHover()  { return getCssVar("--blue-hover", "#0f6e56"); },
  get green()      { return getCssVar("--green",      "#9fe1cb"); },
  get greenHi()    { return getCssVar("--green-hi",   "#22c55e"); },
  get red()        { return getCssVar("--red",        "#ff6b6b"); },
  get amber()      { return getCssVar("--amber",      "#ef9f27"); },
  get muted()      { return getCssVar("--muted",      "var(--text-0-60)"); },
  get accentRgb()  { return getCssVar("--accent-rgb", "29, 158, 117"); },
};
